<template>
  <SideSheet
    class="sidebar-filters"
    data-testid="sidebar-filters"
    position="right"
    :is-open="isOpen"
    @close="$emit('close')"
  >
    <NavBar sticky>
      <template #left>
        <BackButton icon="EF0030" @click="$emit('close')" />
      </template>
      <template #center>{{ filterCount }} Filtros selecionados</template>
    </NavBar>
    <section class="sidebar-filters__content">
      <div
        v-for="(filter, index) in filtersData"
        v-show="filter.options.length > 0"
        :key="index"
        class="sidebar-filters__filter"
      >
        <Heading class="sidebar-filters__filter-title" :level="3">
          {{ filter.label }}
          <ButtonIconContextual
            v-if="filter.details"
            label="Mais informações"
            icon="EF0080"
            size="small"
            @click="openDescriptionModal(filter.label, filter.details)"
          />
        </Heading>
        <SingleSliderFilter
          v-if="
            filter.value === 'minimumInvestment' && isSecondaryMarketEnabled
          "
          :title="filter.options[0].label"
          :min="0"
          :max="50000"
          :step="1000"
          :model-value="Number(filter.selectedFilters)"
          @update:modelValue="
            value => handleSingleSliderChange(value, index, filter)
          "
        />
        <DoubleSliderFilter
          v-else-if="
            filter.value === 'maturityDate' && isSecondaryMarketEnabled
          "
          :left-title="filter.options[0].label"
          :right-title="filter.options[1].label"
          :left-value="getSliderValue(filter.selectedFilters[0])"
          :right-value="getSliderValue(filter.selectedFilters[1], true)"
          :min="0"
          :max="60"
          :step="1"
          @change="payload => handleDoubleSliderChange(payload, index, filter)"
        />
        <FilterList
          v-else
          :checked="filter.selectedFilters"
          class="sidebar-filters__filter-list"
          :options="filter.options"
          :multiple="filter.isMultiple"
          @change="value => handleChange(value, index, filter)"
        />
      </div>
    </section>
    <ButtonContainer class="sidebar-filters__button-container">
      <template #button-primary>
        <ButtonPrimary
          label="Filtrar"
          size="large"
          :disabled="filterCount === 0"
          @click="handleFilters"
        />
      </template>
      <template #button-secondary>
        <ButtonSecondary size="large" label="Limpar" @click="clearFilters" />
      </template>
    </ButtonContainer>
    <Modal
      :is-open="isDescriptionModalOpen"
      size="md"
      class="modal_filter"
      @close="isDescriptionModalOpen = false"
    >
      <Heading :level="3">{{ selectedFilterDetails?.label }}</Heading>
      <div v-for="detail in selectedFilterDetails?.details" :key="detail.title">
        <BaseText class="modal_filter-detail_strong">
          {{ detail.title }}
          <span class="modal_filter-detail_normal">
            {{ detail.description }}
          </span>
        </BaseText>
      </div>
    </Modal>
  </SideSheet>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';

import {
  NavBar,
  Heading,
  FilterList,
  Modal,
  ButtonContainer,
  ButtonPrimary,
  ButtonSecondary,
  ButtonIconContextual,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { BackButton } from '@/modules/common/components/buttons';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';
import SingleSliderFilter from '../SingleSliderFilter/SingleSliderFilter.vue';
import DoubleSliderFilter from '../DoubleSliderFilter/DoubleSliderFilter.vue';

interface IFilter {
  label: string;
  isMultiple: boolean;
  value: string;
  selectedFilters: string | string[] | number;
  options: { label: string; value: string }[];
  details?: { title: string; description: string }[];
}

interface EmitEvents {
  (event: 'apply-filters', filters: IFilter[], count: number): void;
  (event: 'close'): void;
}
const emit = defineEmits<EmitEvents>();

interface Props {
  isOpen: boolean;
  filters: IFilter[];
  clear?: boolean;
}
const props = withDefaults(defineProps<Props>(), { clear: false });

const isDescriptionModalOpen = ref(false);

interface IDescriptionModal {
  label: string;
  details: { title: string; description: string }[];
}
const selectedFilterDetails = ref<IDescriptionModal | null>({
  label: '',
  details: []
});

const isSecondaryMarketEnabled = computed(() =>
  FeatureFlagsModule.isEnabled(FeatureFlags.ENABLE_SECONDARY_MARKET)
);

function handleSingleSliderChange(value: number, index: number) {
  filtersData.value[index].selectedFilters = String(value);
}

function handleDoubleSliderChange(
  payload: { left: number; right: number },
  index: number
) {
  const leftValue = payload.left === undefined ? '' : String(payload.left);
  const rightValue = payload.right === undefined ? '' : String(payload.right);

  filtersData.value[index].selectedFilters = [leftValue, rightValue];
}

function handleChange(value: string, index: number, filter: IFilter) {
  if (filter.isMultiple) {
    filtersData.value[index].selectedFilters = value;
  } else if (filtersData.value[index].selectedFilters === value) {
    filtersData.value[index].selectedFilters = '';
  } else filtersData.value[index].selectedFilters = value;
}

function openDescriptionModal(
  label: string,
  details: { title: string; description: string }[]
) {
  isDescriptionModalOpen.value = true;
  selectedFilterDetails.value = { label, details };
}

watch(
  () => props.clear,
  clear => {
    if (clear) {
      clearFilters();
    }
  },
  { immediate: true }
);

const filtersData = ref<IFilter[]>([]);

const filterCount = computed(() => {
  let count = 0;

  filtersData.value.forEach(filter => {
    if (
      filter.value === 'minimumInvestment' &&
      Number(filter.selectedFilters) === 0
    )
      return;
    if (!filter.isMultiple && filter.selectedFilters) count += 1;
    else count += filter.selectedFilters.length;
  });

  return count;
});

const getSliderValue = (
  value: string | number | string[]
): number | undefined => {
  if (typeof value !== 'string' || value === '') {
    return undefined;
  }

  const num = Number(value);
  return isNaN(num) ? undefined : num;
};

function handleFilters() {
  const appliedFilters: IFilter[] = [];
  const filtersToEmit: IFilter[] = [];
  filtersData.value.forEach(filter => {
    if (filter.value === 'minimumInvestment') {
      if (Number(filter.selectedFilters) > 0) {
        appliedFilters.push(filter);
        filtersToEmit.push(filter);
      }
      return;
    }

    if (filter.value === 'maturityDate') {
      const selected = filter.selectedFilters;

      if (Array.isArray(selected) && selected.length === 2) {
        appliedFilters.push(filter);
        filtersToEmit.push(
          {
            ...filter,
            value: 'minimumMaturity',
            selectedFilters: selected[0],
            isMultiple: false,
            options: []
          },
          {
            ...filter,
            value: 'maximumMaturity',
            selectedFilters: selected[1],
            isMultiple: false,
            options: []
          }
        );
      }
      return;
    }

    if (Array.isArray(filter.selectedFilters)) {
      if (filter.selectedFilters.length > 0) {
        appliedFilters.push(filter);
        filtersToEmit.push(filter);
      }
    } else if (String(filter.selectedFilters).length > 0) {
      appliedFilters.push(filter);
      filtersToEmit.push(filter);
    }
  });

  emit('apply-filters', filtersToEmit, filterCount.value);
}

function clearFilters() {
  filtersData.value = filtersData.value.map(filter => {
    return {
      ...filter,
      selectedFilters: filter.isMultiple ? [] : ''
    };
  });

  handleFilters();
}

onMounted(() => {
  filtersData.value = props.filters;
});
</script>

<style lang="less" scoped>
.sidebar-filters {
  :deep(.panel-content.right) {
    width: 698px;
  }

  :deep(.list) {
    flex-wrap: wrap;
  }

  :deep(.list li) {
    margin-left: 0;
    margin-right: @size-spacing-x300;
    margin-bottom: @size-spacing-x300;
  }

  &__content {
    padding: @size-spacing-x1000 @size-spacing-x1200 @size-spacing-x600;
  }

  &__filter {
    margin-bottom: @size-spacing-x800;
    border-bottom: @size-border-x400 @divider-primary solid;

    &:last-of-type {
      margin-bottom: 0;
      border-bottom: none;
    }

    &-title {
      margin-bottom: @size-spacing-x500;
    }

    &-list {
      margin-bottom: @size-spacing-x700;

      &:last-of-type {
        border-bottom: none;
      }
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 698px;
    position: sticky;
    bottom: 0;
    z-index: 2;
  }
}

.modal_filter {
  :deep(.modal-content) {
    text-align: left;
    align-items: start;

    > * {
      margin-bottom: @size-spacing-x500;
    }
  }

  &-detail_strong {
    font-weight: bold;
  }

  &-detail_normal {
    font-weight: normal;
  }
}
</style>
